import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useAuthSync } from '../../hooks/useAuthSync';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BubbleController,
  ScatterController,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Chart, Line, Pie } from 'react-chartjs-2';
import FinancialReportsService from '../../services/financialReportsService';
import UserService from '../../services/userService';
import ReportControls from './ReportControls';
import Button from '../common/Button';
import './FinancialReports.css';
import { formatDateTimeInTimezone } from '../../utils/dateUtils';
import { useOrganizationTimezone } from '../../hooks/useOrganizationTimezone';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BubbleController,
  ScatterController,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const FinancialReports = () => {
  const auth = useAuth();
  const { getAppRole, user, hasPermission } = useAuthSync();
  const appRole = getAppRole();
  const normalizedAppRole = (appRole || '').toLowerCase();
  const normalizedUserRole = (user?.role || '').toLowerCase();
  const isAdminOrOwner = ['admin', 'owner'].includes(normalizedAppRole) || ['admin', 'owner'].includes(normalizedUserRole);
  const canViewAll = isAdminOrOwner || hasPermission('reports:viewfinancial');
  const isRestricted = !canViewAll;

  const [reportType, setReportType] = useState('daily'); // 'daily' or 'monthly'
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [paymentFormMetric, setPaymentFormMetric] = useState('revenue'); // 'revenue', 'netRevenue', 'refunds'
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('all');
  const debugEnabled = new URLSearchParams(window.location.search).get('debugReports') === '1';
  const { timezone } = useOrganizationTimezone();
  const [isExporting, setIsExporting] = useState(false);

  // Load doctors list
  useEffect(() => {
    const fetchDoctors = async () => {
      if (auth.user?.access_token) {
        if (isRestricted) return;

        try {
          const users = await UserService.getOrganizationUsers(auth.user.access_token);
          setDoctors(users || []);
        } catch (err) {
          console.error('Error loading doctors:', err);
          // Fallback to getDoctors if getOrganizationUsers fails or for non-admin users if that endpoint is restricted
          try {
             // Try to get at least doctors if the full organization list is not available
             const doctorsResponse = await UserService.getDoctors(auth.user.access_token);
             // Verify structure of doctorsResponse
             const doctorsList = doctorsResponse.users || doctorsResponse.data || doctorsResponse; 
             if (Array.isArray(doctorsList)) {
                setDoctors(doctorsList);
             }
          } catch (e) {
             console.error('Error loading doctors fallback:', e);
          }
        }
      }
    };
    fetchDoctors();
  }, [auth.user?.access_token, isRestricted]);

  // Set default doctor for restricted users
  useEffect(() => {
    if (isRestricted && auth.user?.profile?.sub) {
      setSelectedDoctor(auth.user.profile.sub);
    }
  }, [isRestricted, auth.user?.profile?.sub]);

  // Handle window resize for responsive charts
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to format dates properly avoiding timezone issues
  // eslint-disable-next-line no-unused-vars
const formatDateSafely = (dateString) => {
    // If the date is already in YYYY-MM-DD format without time, use it directly
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('es-ES');
    }
    
    // For full ISO strings, parse carefully to avoid timezone shifts
    if (typeof dateString === 'string' && dateString.includes('T')) {
      // Extract just the date part before the 'T'
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day)).toLocaleDateString('es-ES');
    }
    
    // Fallback to regular date parsing
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
  };

  const formatCurrency = (amount) => {
    return (amount || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Load report data
  const loadReportData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.debug('[FinancialReports] loadReportData', {
        reportType,
        selectedYear,
        selectedMonth,
        selectedDoctor,
        appRole,
        userRole: user?.role,
        isRestricted
      });

      let data;
      if (reportType === 'daily') {
        data = await FinancialReportsService.getDailyFinancialReport(
          auth.user?.access_token,
          selectedYear,
          selectedMonth,
          selectedDoctor
        );
      } else {
        data = await FinancialReportsService.getMonthlyFinancialReport(
          auth.user?.access_token,
          selectedYear,
          selectedDoctor
        );
      }

      console.debug('[FinancialReports] report loaded', {
        summary: data?.summary,
        dailyCount: data?.dailyData?.length,
        monthlyCount: data?.monthlyData?.length
      });

      setReportData(data);
    } catch (error) {
      console.error('Error loading report data:', error);
      setError('Error al cargar los datos del reporte');
    } finally {
      setIsLoading(false);
    }
  }, [reportType, selectedYear, selectedMonth, selectedDoctor, auth.user?.access_token, appRole, user?.role, isRestricted]);

  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return '';
    const stringValue = String(value);
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  };

  const handleExportPayments = async () => {
    if (!auth.user?.access_token || isExporting) return;
    setIsExporting(true);

    try {
      const exportData = await FinancialReportsService.getMonthlyPaymentsExport(
        auth.user.access_token,
        selectedYear,
        selectedMonth,
        selectedDoctor
      );

      const payments = exportData?.payments || [];
      const tz = exportData?.period?.timezone || timezone;

      const headers = [
        'ID de Pago',
        'Paciente',
        'Forma de Pago',
        'Monto',
        'Monto Reembolsado',
        'Monto Neto',
        'Fecha de Reembolso',
        'Fecha de Creación',
        'Hora de Creación',
        'Fecha de Pago',
        'Asignado a',
        'Nota'
      ];

      const rows = payments.map(p => {
        const created = formatDateTimeInTimezone(p.createdAt, tz);
        const paymentDate = p.paymentDate
          ? formatDateTimeInTimezone(p.paymentDate, tz)
          : created;
        return [
          p.id,
          p.patientName,
          p.paymentFormName,
          p.amount,
          p.refundAmount,
          p.netAmount,
          p.refundDate,
          created.date,
          created.time,
          paymentDate.date,
          p.allocationUserName,
          p.note
        ].map(escapeCsvValue).join(',');
      });

      const csvContent = [headers.join(','), ...rows].join('\n');
      const blob = new Blob(["\uFEFF", csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const monthLabel = String(selectedMonth).padStart(2, '0');
      link.href = url;
      link.download = `payments_${selectedYear}-${monthLabel}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting payments:', error);
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (auth.user?.access_token) {
      loadReportData();
    }
  }, [auth.user?.access_token, loadReportData]);

  // Prepare bubble chart data for daily view
  const getDailyBubbleChartData = () => {
    if (!reportData || !reportData.dailyData) return null;

    // eslint-disable-next-line no-unused-vars
    const bubbleData = reportData.dailyData.map(day => ({

      x: day.day,
      y: day.revenue,
      r: Math.max(5, Math.min(30, day.paymentsCount * 3)), // Radius based on number of payments
      paymentsCount: day.paymentsCount // Include actual payment count for tooltip
    }));

    // Prepare line data to connect the dots
    const lineData = reportData.dailyData.map(day => ({
      x: day.day,
      y: day.revenue
    }));

    const netRevenueData = reportData.dailyData.map(day => ({
      x: day.day,
      y: day.netRevenue !== undefined ? day.netRevenue : day.revenue
    }));

    const refundsData = reportData.dailyData.map(day => ({
      x: day.day,
      y: day.refunds || 0
    }));

    return {
      datasets: [
        {
          type: 'line',
          label: 'Ingresos Netos',
          data: netRevenueData,
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.1)',
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
          fill: false,
          tension: 0.3,
        },
        {
          type: 'line',
          label: 'Ingresos Totales',
          data: lineData,
          borderColor: 'rgba(54, 162, 235, 0.8)',
          backgroundColor: 'rgba(54, 162, 235, 0.1)',
          borderWidth: 2,
          pointRadius: 0,
          pointHoverRadius: 0,
          fill: false,
          tension: 0.3,
          hidden: true,
        },
        {
          type: 'line',
          label: 'Reembolsos',
          data: refundsData,
          borderColor: 'rgba(255, 99, 132, 1)',
          backgroundColor: 'rgba(255, 99, 132, 0.1)',
          borderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
          fill: false,
          tension: 0.3,
          hidden: true,
        },
      ],
    };
  };

  // Prepare line chart data for monthly view
  const getMonthlyLineChartData = () => {
    if (!reportData || !reportData.monthlyData) return null;

    return {
      labels: reportData.monthlyData.map(month => month.monthName),
      datasets: [
        {
          label: 'Ingresos Mensuales',
          data: reportData.monthlyData.map(month => month.revenue),
          borderColor: 'rgba(75, 192, 192, 1)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          borderWidth: 3,
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBackgroundColor: 'rgba(75, 192, 192, 1)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          fill: true,
          tension: 0.3,
        },
      ],
    };
  };

  // Prepare pie chart data for payment forms
  const getPaymentFormPieData = () => {
    if (!reportData || !reportData.paymentFormData || reportData.paymentFormData.length === 0) return null;

    const colors = [
      'rgba(255, 99, 132, 0.8)',
      'rgba(54, 162, 235, 0.8)',
      'rgba(255, 206, 86, 0.8)',
      'rgba(75, 192, 192, 0.8)',
      'rgba(153, 102, 255, 0.8)',
      'rgba(255, 159, 64, 0.8)',
      'rgba(199, 199, 199, 0.8)',
      'rgba(83, 102, 255, 0.8)',
      'rgba(255, 99, 255, 0.8)',
      'rgba(99, 255, 132, 0.8)',
    ];

    let label = 'Ingresos Totales por Forma de Pago';
    let data = reportData.paymentFormData.map(form => form.revenue);

    if (paymentFormMetric === 'netRevenue') {
      label = 'Ingresos Netos por Forma de Pago';
      data = reportData.paymentFormData.map(form => form.netRevenue || 0);
    } else if (paymentFormMetric === 'refunds') {
      label = 'Reembolsos por Forma de Pago';
      data = reportData.paymentFormData.map(form => form.refunds || 0);
    }

    return {
      labels: reportData.paymentFormData.map(form => form.paymentFormName),
      datasets: [
        {
          label,
          data,
          backgroundColor: colors.slice(0, reportData.paymentFormData.length),
          borderColor: colors.slice(0, reportData.paymentFormData.length).map(color => color.replace('0.8', '1')),
          borderWidth: 2,
        },
      ],
    };
  };

  const bubbleChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: isMobile ? 8 : 12,
          font: {
            size: isMobile ? 10 : 12
          }
        }
      },
      title: {
        display: true,
        text: `Ingresos Diarios - ${reportData?.period?.monthName} ${reportData?.period?.year}`,
        font: {
          size: isMobile ? 14 : 16
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const point = context.raw;
            if (context.dataset.type === 'bubble') {
              return [
                `Día: ${point.x}`,
                `Ingresos: $${formatCurrency(point.y)}`,
                `Pagos: ${point.paymentsCount || 0}`
              ];
            }
            return `${context.dataset.label}: $${formatCurrency(point.y)}`;
          }
        }
      }
    },
    scales: {
      x: {
        type: 'linear',
        position: 'bottom',
        title: {
          display: true,
          text: 'Día del Mes',
          font: {
            size: isMobile ? 10 : 12
          }
        },
        min: 1,
        max: reportData?.period?.daysInMonth || 31,
        ticks: {
          font: {
            size: isMobile ? 9 : 11
          }
        }
      },
      y: {
        title: {
          display: true,
          text: 'Ingresos ($)',
          font: {
            size: isMobile ? 10 : 12
          }
        },
        beginAtZero: true,
        ticks: {
          font: {
            size: isMobile ? 9 : 11
          }
        }
      }
    },
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: isMobile ? 8 : 12,
          font: {
            size: isMobile ? 10 : 12
          }
        }
      },
      title: {
        display: true,
        text: `Ingresos Mensuales - ${selectedYear}`,
        font: {
          size: isMobile ? 14 : 16
        }
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Ingresos ($)',
          font: {
            size: isMobile ? 10 : 12
          }
        },
        ticks: {
          font: {
            size: isMobile ? 9 : 11
          }
        }
      },
      x: {
        ticks: {
          font: {
            size: isMobile ? 9 : 11
          }
        }
      }
    },
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: isMobile ? 'bottom' : 'right',
        labels: {
          boxWidth: isMobile ? 10 : 15,
          font: {
            size: isMobile ? 10 : 12
          },
          padding: isMobile ? 8 : 15
        }
      },
      title: {
        display: true,
        text: 'Distribución por Forma de Pago',
        font: {
          size: isMobile ? 14 : 16
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(2) : 0;
            return `${label}: $${formatCurrency(value)} (${percentage}%)`;
          }
        }
      }
    },
  };

  return (
    <div className="financial-reports">
      <div className="reports-header">
        <h1>Reportes Financieros</h1>
        
        <ReportControls
          reportType={reportType}
          setReportType={setReportType}
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          selectedMonth={selectedMonth}
          setSelectedMonth={setSelectedMonth}
          onRefresh={loadReportData}
          isLoading={isLoading}
          doctors={doctors}
          selectedDoctor={selectedDoctor}
          setSelectedDoctor={setSelectedDoctor}
          canSelectDoctor={!isRestricted}
          showGeneralDoctorOption
        />
      </div>

      {debugEnabled && (
        <div className="debug-panel" style={{ marginBottom: '1rem', padding: '0.75rem 1rem', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fafafa', fontSize: '12px' }}>
          <div><strong>Debug (reports)</strong></div>
          <div>reportType: {reportType}</div>
          <div>year: {selectedYear}</div>
          <div>month: {selectedMonth}</div>
          <div>doctorId: {selectedDoctor}</div>
          <div>appRole: {appRole || 'n/a'}</div>
          <div>userRole: {user?.role || 'n/a'}</div>
          <div>isRestricted: {String(isRestricted)}</div>
          <div>summary.totalPayments: {reportData?.summary?.totalPayments ?? 'n/a'}</div>
          <div>summary.totalRevenue: {reportData?.summary?.totalRevenue ?? 'n/a'}</div>
        </div>
      )}

      {/* Loading and Error States */}
      {isLoading && (
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Cargando reporte...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <i className="fas fa-exclamation-triangle"></i>
          <p>{error}</p>
        </div>
      )}

      {/* Report Content */}
      {reportData && !isLoading && (
        <div className="reports-content">
          {/* Summary Cards */}
          <div className="summary-cards">
            <div className="summary-card">
              <h3>Ingresos Netos</h3>
              <p className="amount text-primary">${formatCurrency(reportData.summary?.totalNetRevenue ?? reportData.summary?.totalRevenue)}</p>
            </div>
            <div className="summary-card">
              <h3>Ingresos Totales</h3>
              <p className="amount text-success">
                ${formatCurrency(reportData.summary?.totalRevenue)}
                {reportData.summary?.totalPayments !== undefined && (
                  <span className="count" style={{ marginLeft: '6px', fontSize: '0.85em', color: '#6c757d' }}>
                    ({reportData.summary?.totalPayments})
                  </span>
                )}
              </p>
            </div>
            <div className="summary-card">
              <h3>Reembolsos</h3>
              <p className="amount text-danger">
                -${formatCurrency(reportData.summary?.totalRefunds || 0)}
                {reportData.summary?.totalRefundsCount !== undefined && (
                  <span className="count" style={{ marginLeft: '6px', fontSize: '0.85em', color: '#6c757d' }}>
                    ({reportData.summary?.totalRefundsCount})
                  </span>
                )}
              </p>
            </div>
            <div className="summary-card">
              <h3>Promedio por Pago</h3>
              <p className="amount">${formatCurrency(reportData.summary?.avgPaymentAmount)}</p>
            </div>
          </div>

          {/* Chart */}
          <div className="chart-container">
            {reportType === 'daily' ? (
              getDailyBubbleChartData() && (
                <Chart 
                  type="scatter"
                  data={getDailyBubbleChartData()} 
                  options={bubbleChartOptions} 
                />
              )
            ) : (
              getMonthlyLineChartData() && (
                <Line 
                  data={getMonthlyLineChartData()} 
                  options={lineChartOptions} 
                />
              )
            )}
          </div>

          {/* Payment Form Distribution Section */}
          {reportData.paymentFormData && reportData.paymentFormData.length > 0 && (
            <div className="payment-forms-section">
              <h3>Distribución por Forma de Pago</h3>
              <div className="payment-forms-controls" style={{ padding: '1.5rem 2rem 0' }}>
                <select 
                  value={paymentFormMetric} 
                  onChange={(e) => setPaymentFormMetric(e.target.value)}
                  className="form-select"
                  style={{ minWidth: '200px' }}
                >
                  <option value="revenue">Ingresos Totales</option>
                  <option value="netRevenue">Ingresos Netos</option>
                  <option value="refunds">Reembolsos</option>
                </select>
              </div>
              
              <div className="payment-forms-content">
                {/* Payment Forms Table */}
                <div className="payment-forms-table-container">
                  <div className="table-wrapper">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Forma de Pago</th>
                          <th>
                            {paymentFormMetric === 'revenue' ? 'Ingresos' : 
                             paymentFormMetric === 'netRevenue' ? 'Ingresos Netos' : 'Reembolsos'}
                          </th>
                          <th># Movimientos</th>
                          <th>Porcentaje</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.paymentFormData.map((form, index) => {
                          let value = form.revenue;
                          let total = reportData.summary.totalRevenue;
                          
                          if (paymentFormMetric === 'netRevenue') {
                            value = form.netRevenue || 0;
                            total = reportData.summary.totalNetRevenue;
                          } else if (paymentFormMetric === 'refunds') {
                            value = form.refunds || 0;
                            total = reportData.summary.totalRefunds;
                          }
                          
                          const percentage = total > 0 ? ((value / total) * 100) : 0;

                          return (
                            <tr key={form.paymentFormId || index}>
                              <td>{form.paymentFormName}</td>
                              <td>${formatCurrency(value)}</td>
                              <td>{form.paymentsCount}</td>
                              <td>{percentage.toFixed(2)}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Payment Forms Pie Chart */}
                <div className="payment-forms-chart-container">
                  {getPaymentFormPieData() && (
                    <Pie 
                      data={getPaymentFormPieData()} 
                      options={pieChartOptions} 
                    />
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Data Table */}
          <div className="data-table-container">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <h3 style={{ margin: 0 }}>Datos Detallados</h3>
              <Button
                variant="outline"
                size="sm"
                icon="fas fa-file-csv"
                onClick={handleExportPayments}
                disabled={isExporting}
              >
                Exportar pagos
              </Button>
            </div>
            <div className="table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    {reportType === 'daily' ? (
                      <>
                        <th>Día</th>
                        {!isMobile && <th>Ingresos</th>}
                        {!isMobile && <th>Reembolsos ($)</th>}
                        <th>Neto</th>
                        <th>Pagos</th>
                        {!isMobile && <th>Reembolsos</th>}
                      </>
                    ) : (
                      <>
                        <th>Mes</th>
                        {!isMobile && <th>Ingresos</th>}
                        {!isMobile && <th>Reembolsos ($)</th>}
                        <th>Neto</th>
                        <th>Pagos</th>
                        {!isMobile && <th>Reembolsos</th>}
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {reportType === 'daily' 
                    ? reportData.dailyData?.map(day => (
                        <tr key={day.day}>
                          <td>{day.day}</td>
                          {!isMobile && <td className="text-success">${formatCurrency(day.revenue)}</td>}
                          {!isMobile && <td className="text-danger">-${formatCurrency(day.refunds || 0)}</td>}
                          <td className="font-bold">${formatCurrency(day.netRevenue || day.revenue)}</td>
                          <td>{day.paymentsCount}</td>
                          {!isMobile && <td>{day.refundsCount || 0}</td>}
                        </tr>
                      ))
                    : reportData.monthlyData?.map(month => (
                        <tr key={month.month}>
                          <td>{month.monthName}</td>
                          {!isMobile && <td className="text-success">${formatCurrency(month.revenue)}</td>}
                          {!isMobile && <td className="text-danger">-${formatCurrency(month.refunds || 0)}</td>}
                          <td className="font-bold">${formatCurrency(month.netRevenue || month.revenue)}</td>
                          <td>{month.paymentsCount}</td>
                          {!isMobile && <td>{month.refundsCount || 0}</td>}
                        </tr>
                      ))
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialReports;
